import { useState, useEffect } from "react";
import wsManager from "../../../utils/websocketManager";

/**
 * Custom hook to manage all WebSocket events for Kanban
 * Handles card animations, state updates, and metrics recalculation
 *
 * @param {Object} cards - Current cards state from parent
 * @param {Function} setCards - State setter for cards
 * @param {Function} setMetrics - State setter for metrics
 * @param {string} selectedCategory - Currently selected category filter
 * @returns {Object} { newCardIds, removingCardIds }
 */
export const useKanbanWebSocketEvents = (
  cards,
  setCards,
  setMetrics,
  selectedCategory,
) => {
  const [newCardIds, setNewCardIds] = useState(new Set());
  const [removingCardIds, setRemovingCardIds] = useState(new Set());
  const [movingCardIds, setMovingCardIds] = useState(new Set());

  /**
   * Helper: Calculate metrics from cards object
   */
  const calculateMetrics = (cardsState) => {
    if (!cardsState) return null;
    return {
      warehouse_value: {
        value: (cardsState.inStock || []).reduce(
          (sum, card) =>
            sum + Number(card.variant_price || 0) * Number(card.qty || 1),
          0,
        ),
        count: (cardsState.inStock || []).length,
      },
      shipment_value: {
        value: (cardsState.inTransit || []).reduce(
          (sum, card) =>
            sum + Number(card.variant_price || 0) * Number(card.qty || 1),
          0,
        ),
        count: (cardsState.inTransit || []).length,
      },
      pipeline_value: {
        value: Object.values(cardsState).reduce(
          (a, b) =>
            a +
            (b || []).reduce(
              (sum, card) =>
                sum + Number(card.qty || 1) * Number(card.variant_price || 0),
              0,
            ),
          0,
        ),
        count: Object.values(cardsState).reduce(
          (a, b) => a + (b || []).length,
          0,
        ),
      },
      throughput: (cardsState.delivered || []).length,
    };
  };

  /**
   * Helper: Animate card addition (slide-in from left)
   */
  const animateCardAddition = (cardIds) => {
    const newIds = new Set(cardIds);
    setNewCardIds((prev) => new Set([, ...newIds, ...prev]));

    setTimeout(() => {
      setNewCardIds((prev) => {
        const updated = new Set(prev);
        newIds.forEach((id) => updated.delete(id));
        return updated;
      });
    }, 600); // Match animation duration
  };

  /**
   * Helper: Animate card removal (fade-out)
   */
  const animateCardRemoval = (cardIds) => {
    const removeIds = new Set(cardIds);
    setRemovingCardIds((prev) => new Set([...removeIds, ...prev]));

    setTimeout(() => {
      setRemovingCardIds((prev) => {
        const updated = new Set(prev);
        removeIds.forEach((id) => updated.delete(id));
        return updated;
      });
    }, 400); // Match removal animation duration
  };

  /**
   * Helper: Check if user is on "/" and page is active
   */
  const isPageActive = () => {
    const currentPath = localStorage.getItem("path");
    return currentPath === "/";
  };

  // ── Listener 1: Fresh kanban data refresh ──────────────────────
  useEffect(() => {
    const unsub = wsManager.on("kanbanDataRefresh", (msg) => {
      const msgCategory = msg.category === "all" ? "all" : msg.category;
      console.log(msg);

      if (msgCategory === selectedCategory) {
        console.log("📡 [Kanban WS] Received fresh data via WebSocket");
        setCards(msg.data);
        setMetrics(calculateMetrics(msg.data));
      }
    });

    return () => {
      unsub();
    };
  }, [selectedCategory, setCards, setMetrics]);

  // ── Listener 2: PO Issued updates (new cards added to poIssued stage) ────
  useEffect(() => {
    const unsub = wsManager.on("kanbanPoIssuedUpdate", (msg) => {
      if (!isPageActive()) {
        console.log(
          "📡 [Kanban WS] poIssued update ignored (not on / or tab inactive)",
        );
        return;
      }

      if (!msg.poIssued || !msg.poIssued.length) return;

      console.log(
        `📡 [Kanban WS] Received ${msg.poIssued.length} new poIssued card(s)`,
      );

      // Animate addition
      animateCardAddition(msg.poIssued.map((card) => card._id));

      setCards((prev) => {
        if (!prev || !prev.poIssued) return prev;

        // Filter out duplicates: check if uuid + sku combination already exists
        const existingSet = new Set(
          (prev.poIssued || []).map((card) => `${card.uuid}::${card.sku}`),
        );

        const newCards = msg.poIssued.filter((card) => {
          const key = `${card.uuid}::${card.sku}`;
          return !existingSet.has(key);
        });

        if (newCards.length === 0) {
          console.log(
            "📡 [Kanban WS] No new unique cards to add (all duplicates filtered)",
          );
          return prev;
        }

        const updated = {
          ...prev,
          poIssued: [...newCards, ...prev.poIssued],
        };

        setMetrics(calculateMetrics(updated));
        return updated;
      });
    });

    return () => {
      unsub();
    };
  }, []);

  // ── Listener 3: Supplier Confirmed updates (move from poIssued to supplierConfirmed) ────
  useEffect(() => {
    const unsub = wsManager.on("kanbanSupplierConfirmedUpdate", (msg) => {
      if (!isPageActive()) {
        console.log(
          "📡 [Kanban WS] Supplier Confirmed update ignored (not on / or tab inactive)",
        );
        return;
      }

      if (!msg.removed || !msg.added) return;

      const { removed, added } = msg;
      const cardIds = (added.cards || []).map((card) => card._id);
      console.log(
        `📡 [Kanban WS] Moving ${removed.uuid} from ${removed.stage} to ${added.stage}`,
      );

      // Mark as moving for coordinated animation
      setMovingCardIds((prev) => new Set([...cardIds, ...prev]));

      // Remove animation (exit from old stage)
      animateCardRemoval(cardIds);

      // Animate entry after a delay (staggered effect)
      setTimeout(() => {
        animateCardAddition(cardIds);
      }, 300);

      // Clear moving state after animations complete
      setTimeout(() => {
        setMovingCardIds((prev) => {
          const updated = new Set(prev);
          cardIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 1100);

      setCards((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };

        // 1. Remove card(s) from the source stage
        if (updated[removed.stage]) {
          updated[removed.stage] = updated[removed.stage].filter(
            (card) => card.uuid !== removed.uuid,
          );
        }

        // 2. Add card(s) to the destination stage (check for duplicates: uuid + sku)
        if (updated[added.stage]) {
          const existingSet = new Set(
            (updated[added.stage] || []).map(
              (card) => `${card.uuid}::${card.sku}`,
            ),
          );

          const cardsToAdd = (added.cards || []).filter((card) => {
            const key = `${card.uuid}::${card.sku}`;
            return !existingSet.has(key);
          });

          if (cardsToAdd.length > 0) {
            updated[added.stage] = [...cardsToAdd, ...updated[added.stage]];
          }
        } else {
          updated[added.stage] = added.cards || [];
        }

        // 3. Recalculate metrics
        setMetrics(calculateMetrics(updated));

        return updated;
      });
    });

    return () => {
      unsub();
    };
  }, []);

  // ── Listener 4: In Transit updates (move from supplierConfirmed to inTransit) ────
  useEffect(() => {
    const unsub = wsManager.on("kanbanInTransitUpdate", (msg) => {
      if (!isPageActive()) {
        console.log(
          "📡 [Kanban WS] In Transit update ignored (not on / or tab inactive)",
        );
        return;
      }

      if (!msg.removed || !msg.added) return;

      const { removed, added } = msg;
      const cardIds = (added.cards || []).map((card) => card._id);
      console.log(
        `📡 [Kanban WS] Moving ${removed.uuid} from ${removed.stage} to ${added.stage}`,
      );

      // Mark as moving for coordinated animation
      setMovingCardIds((prev) => new Set([...cardIds, ...prev]));

      // Remove animation (exit from old stage)
      animateCardRemoval(cardIds);

      // Animate entry after a delay (staggered effect)
      setTimeout(() => {
        animateCardAddition(cardIds);
      }, 300);

      // Clear moving state after animations complete
      setTimeout(() => {
        setMovingCardIds((prev) => {
          const updated = new Set(prev);
          cardIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 1100);

      setCards((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };

        // 1. Remove card(s) from the source stage
        if (updated[removed.stage]) {
          updated[removed.stage] = updated[removed.stage].filter(
            (card) => card.uuid !== removed.uuid,
          );
        }

        // 2. Add card(s) to the destination stage (check for duplicates: uuid + sku)
        if (updated[added.stage]) {
          const existingSet = new Set(
            (updated[added.stage] || []).map(
              (card) => `${card.uuid}::${card.sku}`,
            ),
          );

          const cardsToAdd = (added.cards || []).filter((card) => {
            const key = `${card.uuid}::${card.sku}`;
            return !existingSet.has(key);
          });

          if (cardsToAdd.length > 0) {
            updated[added.stage] = [...cardsToAdd, ...updated[added.stage]];
          }
        } else {
          updated[added.stage] = added.cards || [];
        }

        // 3. Recalculate metrics
        setMetrics(calculateMetrics(updated));

        return updated;
      });
    });

    return () => {
      unsub();
    };
  }, []);

  // ── Listener 5: Receiving updates (move from inTransit to receiving) ────
  useEffect(() => {
    const unsub = wsManager.on("kanbanReceivingUpdate", (msg) => {
      if (!isPageActive()) {
        console.log(
          "📡 [Kanban WS] Receiving update ignored (not on / or tab inactive)",
        );
        return;
      }

      if (!msg.removed || !msg.added) return;

      const { removed, added } = msg;
      const cardIds = (added.cards || []).map((card) => card._id);
      console.log(
        `📡 [Kanban WS] Moving ${removed.uuid} from ${removed.stage} to ${added.stage}`,
      );

      // Mark as moving for coordinated animation
      setMovingCardIds((prev) => new Set([...cardIds, ...prev]));

      // Remove animation (exit from old stage)
      animateCardRemoval(cardIds);

      // Animate entry after a delay (staggered effect)
      setTimeout(() => {
        animateCardAddition(cardIds);
      }, 300);

      // Clear moving state after animations complete
      setTimeout(() => {
        setMovingCardIds((prev) => {
          const updated = new Set(prev);
          cardIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 1100);

      setCards((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };

        // 1. Remove card(s) from the source stage
        if (updated[removed.stage]) {
          updated[removed.stage] = updated[removed.stage].filter(
            (card) => card.uuid !== removed.uuid,
          );
        }

        // 2. Add card(s) to the destination stage (check for duplicates: uuid + sku)
        if (updated[added.stage]) {
          const existingSet = new Set(
            (updated[added.stage] || []).map(
              (card) => `${card.uuid}::${card.sku}`,
            ),
          );

          const cardsToAdd = (added.cards || []).filter((card) => {
            const key = `${card.uuid}::${card.sku}`;
            return !existingSet.has(key);
          });

          if (cardsToAdd.length > 0) {
            updated[added.stage] = [...cardsToAdd, ...updated[added.stage]];
          }
        } else {
          updated[added.stage] = added.cards || [];
        }

        // 3. Recalculate metrics
        setMetrics(calculateMetrics(updated));

        return updated;
      });
    });

    return () => {
      unsub();
    };
  }, []);

  // ── Listener 6: Reached Warehouse updates (move from inTransit to receiving) ────
  useEffect(() => {
    const unsub = wsManager.on("kanbanReachedWarehouseUpdate", (msg) => {
      if (!isPageActive()) {
        console.log(
          "📡 [Kanban WS] Reached Warehouse update ignored (not on / or tab inactive)",
        );
        return;
      }

      if (!msg.removed || !msg.added) return;

      const { removed, added } = msg;
      const cardIds = (added.cards || []).map((card) => card._id);
      console.log(
        `📡 [Kanban WS] Moving ${removed.uuid} from ${removed.stage} to ${added.stage}`,
      );

      // Mark as moving for coordinated animation
      setMovingCardIds((prev) => new Set([...cardIds, ...prev]));

      // Remove animation (exit from old stage)
      animateCardRemoval(cardIds);

      // Animate entry after a delay (staggered effect)
      setTimeout(() => {
        animateCardAddition(cardIds);
      }, 300);

      // Clear moving state after animations complete
      setTimeout(() => {
        setMovingCardIds((prev) => {
          const updated = new Set(prev);
          cardIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 1100);

      setCards((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };

        // 1. Remove card(s) from the source stage (inTransit)
        if (updated[removed.stage]) {
          updated[removed.stage] = updated[removed.stage].filter(
            (card) => card.uuid !== removed.uuid,
          );
        }

        // 2. Add card(s) to the destination stage (receiving) - check for duplicates: uuid + sku
        if (updated[added.stage]) {
          const existingSet = new Set(
            (updated[added.stage] || []).map(
              (card) => `${card.uuid}::${card.sku}`,
            ),
          );

          const cardsToAdd = (added.cards || []).filter((card) => {
            const key = `${card.uuid}::${card.sku}`;
            return !existingSet.has(key);
          });

          if (cardsToAdd.length > 0) {
            updated[added.stage] = [...cardsToAdd, ...updated[added.stage]];
          }
        } else {
          updated[added.stage] = added.cards || [];
        }

        // 3. Recalculate metrics
        setMetrics(calculateMetrics(updated));

        return updated;
      });
    });

    return () => {
      unsub();
    };
  }, []);

  return { newCardIds, removingCardIds, movingCardIds };
};
